/**
 * Production-grade Socket.IO connection manager.
 * Handles silent TCP drops (half-open), health checks, and controlled reconnection.
 * Bosph Technologies - Notes+
 */

import { io } from 'socket.io-client';
import { getApiBaseUrlAsync } from '../config/apiConfig';

/** @readonly */
export const ConnectionState = Object.freeze({
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  DISCONNECTED: 'disconnected',
  UNHEALTHY: 'unhealthy',
});

const HEALTH_CHECK_INTERVAL_MS = 30 * 1000;   // 30s - send health:check
const HEALTH_RESPONSE_TIMEOUT_MS = 60 * 1000; // 60s - no response → reconnect
const RECONNECT_BASE_DELAY_MS = 2000;
const RECONNECT_MAX_DELAY_MS = 30000;

class SocketManagerClass {
  constructor() {
    this.socket = null;
    this._state = ConnectionState.DISCONNECTED;
    this._token = null;
    this._listeners = new Set();
    this._healthCheckTimer = null;
    this._healthResponseTimer = null;
    this._reconnectTimer = null;
    this._reconnectScheduled = false;
    this._reconnectAttempt = 0;
    this._isConnecting = false;

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        if (this._token && !this.isConnected()) {
          this._reconnectAttempt = 0;
          this._scheduleReconnect(0);
        }
      });
      window.addEventListener('offline', () => {
        this._clearReconnectTimer();
        this._clearHealthTimers();
        this._teardownSocket();
        this._setState(ConnectionState.DISCONNECTED);
      });
      // ConnectionMonitor: server health restored (LAN may still be "online")
      window.addEventListener('notesplus-server-online', () => {
        if (this._token && !this.isConnected() && !this._isConnecting) {
          this._reconnectAttempt = 0;
          this._scheduleReconnect(0);
        }
      });
      window.addEventListener('notesplus-server-offline', () => {
        // Keep token; stop hammering until monitor says online again.
        // Active socket will disconnect on its own; do not clear token.
        this._clearReconnectTimer();
      });
    }
  }

  getState() {
    return this._state;
  }

  _setState(next) {
    if (this._state === next) return;
    this._state = next;
    this._listeners.forEach((cb) => {
      try { cb(next); } catch (e) { console.warn('[SocketManager] listener error:', e); }
    });
  }

  subscribe(callback) {
    this._listeners.add(callback);
    return () => this._listeners.delete(callback);
  }

  isConnected() {
    return this.socket?.connected === true;
  }

  getSocket() {
    return this.socket;
  }

  _clearHealthTimers() {
    if (this._healthCheckTimer) {
      clearInterval(this._healthCheckTimer);
      this._healthCheckTimer = null;
    }
    if (this._healthResponseTimer) {
      clearTimeout(this._healthResponseTimer);
      this._healthResponseTimer = null;
    }
  }

  _clearReconnectTimer() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._reconnectScheduled = false;
  }

  _reconnectDelayMs() {
    const exp = Math.min(this._reconnectAttempt, 4);
    return Math.min(RECONNECT_MAX_DELAY_MS, RECONNECT_BASE_DELAY_MS * (2 ** exp));
  }

  _startHealthCheck() {
    this._clearHealthTimers();
    if (!this.socket?.connected) return;

    this._healthCheckTimer = setInterval(() => {
      if (!this.socket?.connected) {
        this._clearHealthTimers();
        return;
      }
      if (this._healthResponseTimer) {
        clearTimeout(this._healthResponseTimer);
        this._healthResponseTimer = null;
      }
      this.socket.emit('health:check', { ts: Date.now() });
      this._healthResponseTimer = setTimeout(() => {
        this._healthResponseTimer = null;
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ [SocketManager] Health check timeout - reconnecting');
        }
        this.forceReconnect();
      }, HEALTH_RESPONSE_TIMEOUT_MS);
    }, HEALTH_CHECK_INTERVAL_MS);
  }

  _onHealthOk() {
    if (this._healthResponseTimer) {
      clearTimeout(this._healthResponseTimer);
      this._healthResponseTimer = null;
    }
  }

  _teardownSocket() {
    if (!this.socket) return;
    this._clearHealthTimers();
    this.socket.removeAllListeners();
    try {
      this.socket.disconnect();
    } catch (_) { /* ignore */ }
    this.socket = null;
  }

  /**
   * Initialize or re-initialize socket with token.
   * @param {string} token - JWT
   * @returns {Promise<import("socket.io-client").Socket>}
   */
  async init(token) {
    if (!token) {
      const err = new Error('Socket connection requires token');
      err.code = 'AUTH_REQUIRED';
      throw err;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this._setState(ConnectionState.DISCONNECTED);
      const err = new Error('Offline: cannot initialize socket');
      err.code = 'OFFLINE';
      throw err;
    }

    this._token = token;

    if (this.socket?.connected) {
      return this.socket;
    }

    if (this._isConnecting) {
      return new Promise((resolve) => {
        const unsub = this.subscribe((state) => {
          if (state === ConnectionState.CONNECTED && this.socket) {
            unsub();
            resolve(this.socket);
          }
          if (state === ConnectionState.DISCONNECTED || state === ConnectionState.UNHEALTHY) {
            unsub();
            resolve(null);
          }
        });
        setTimeout(() => {
          unsub();
          resolve(this.socket || null);
        }, 15000);
      });
    }

    this._isConnecting = true;
    this._setState(ConnectionState.CONNECTING);

    try {
      this._teardownSocket();

      const apiBaseUrl = await getApiBaseUrlAsync();
      const baseUrl = (apiBaseUrl || '').replace('/api', '');

      // Own reconnect lifecycle — avoid racing socket.io auto-reconnect + our init
      this.socket = io(baseUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: false,
        autoConnect: true,
      });

      this.socket.on('connect', () => {
        this._isConnecting = false;
        this._reconnectAttempt = 0;
        this._clearReconnectTimer();
        this._setState(ConnectionState.CONNECTED);
        this._startHealthCheck();
      });

      this.socket.on('health:ok', () => {
        this._onHealthOk();
      });

      this.socket.on('disconnect', () => {
        this._isConnecting = false;
        this._clearHealthTimers();
        this._setState(ConnectionState.DISCONNECTED);
        if (this._token) {
          this._scheduleReconnect();
        }
      });

      this.socket.on('connect_error', (err) => {
        this._isConnecting = false;
        this._clearHealthTimers();
        this._setState(ConnectionState.DISCONNECTED);
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ [SocketManager] Connection error:', err?.message || err);
        }
        if (this._token && (typeof navigator === 'undefined' || navigator.onLine)) {
          this._scheduleReconnect();
        }
      });

      await new Promise((resolve, reject) => {
        const t = setTimeout(() => {
          this.socket?.off('connect', onConnect);
          this.socket?.off('connect_error', onErr);
          if (this.socket?.connected) resolve();
          else reject(new Error('Connection timeout'));
        }, 15000);
        const onConnect = () => {
          clearTimeout(t);
          this.socket?.off('connect_error', onErr);
          resolve();
        };
        const onErr = (e) => {
          clearTimeout(t);
          this.socket?.off('connect', onConnect);
          reject(e);
        };
        this.socket.once('connect', onConnect);
        this.socket.once('connect_error', onErr);
      });

      return this.socket;
    } catch (error) {
      this._isConnecting = false;
      this._setState(ConnectionState.DISCONNECTED);
      this._teardownSocket();
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ [SocketManager] Init failed:', error);
      }
      // Keep trying while session is alive — do not leave a dead-end after teardown
      if (this._token && error?.code !== 'OFFLINE' && error?.code !== 'AUTH_REQUIRED') {
        if (typeof navigator === 'undefined' || navigator.onLine) {
          this._scheduleReconnect();
        }
      }
      throw error;
    }
  }

  /**
   * Schedule a single reconnect attempt (deduped). Uses backoff while server is down.
   * @param {number} [delayMs] - optional override (0 = immediate attempt)
   */
  _scheduleReconnect(delayMs) {
    if (!this._token) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this._setState(ConnectionState.DISCONNECTED);
      return;
    }
    if (this.isConnected() || this._isConnecting) return;
    if (this._reconnectScheduled) return;

    const delay = typeof delayMs === 'number' ? delayMs : this._reconnectDelayMs();
    this._reconnectScheduled = true;
    this._setState(ConnectionState.RECONNECTING);

    this._reconnectTimer = setTimeout(async () => {
      this._reconnectTimer = null;
      this._reconnectScheduled = false;

      if (!this._token) return;
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        this._setState(ConnectionState.DISCONNECTED);
        return;
      }
      if (this.isConnected() || this._isConnecting) return;

      this._reconnectAttempt += 1;
      try {
        await this.init(this._token);
      } catch (_) {
        this._setState(ConnectionState.DISCONNECTED);
        // init() already schedules another attempt on failure when token remains
      }
    }, delay);
  }

  /**
   * Force disconnect and re-init with current token.
   * Call before emit when isConnected() is false to recover from silent drop.
   */
  async forceReconnect() {
    this._clearReconnectTimer();
    this._clearHealthTimers();
    this._setState(ConnectionState.RECONNECTING);
    this._teardownSocket();
    if (!this._token) {
      this._setState(ConnectionState.DISCONNECTED);
      return null;
    }
    try {
      await this.init(this._token);
      return this.socket;
    } catch (e) {
      this._setState(ConnectionState.DISCONNECTED);
      return null;
    }
  }

  disconnect() {
    this._token = null;
    this._reconnectAttempt = 0;
    this._isConnecting = false;
    this._clearReconnectTimer();
    this._clearHealthTimers();
    this._teardownSocket();
    this._setState(ConnectionState.DISCONNECTED);
  }
}

export const socketManager = new SocketManagerClass();
